import type { PropertiesOf, Schema } from '@tix/openapi-sdk'

export type FormInputType = 'string' | 'text' | 'number' | 'boolean' | 'tel' | 'date' | 'select' | 'email'

type FormInputMap = {
  [key: string]: FormInputType | FormInputMap
}

export type SurveyFieldSpec = Partial<PropertiesOf<NonNullable<Schema<'ticket_group'>['additional_info_spec']>>> & {
  minLength?: number
  maxLength?: number
}

export interface FieldInputConfig {
  type: FormInputType
  options?: { value: string; label: string }[]
}

const inputTypeMap = {
  date: 'date',
  integer: 'number',
  boolean: 'boolean',
  optionSelector: 'select',
  string: {
    emailAddress: 'email',
    name: 'string',
    paragraph: 'text',
    phoneNumber: 'tel',
    scanCode: 'string',
    string: 'string',
  },
} as const satisfies FormInputMap

export type InputMapKey = keyof typeof inputTypeMap

export function getMapKey(spec: SurveyFieldSpec): InputMapKey {
  if (spec.format === 'date') return 'date'
  if (Array.isArray(spec.enum) && spec.enum.length > 0) return 'optionSelector'
  if (spec.type === 'integer') return 'integer'
  if (spec.type === 'boolean') return 'boolean'
  return 'string'
}

export function getFieldInputConfig(spec: SurveyFieldSpec): FieldInputConfig {
  const key = getMapKey(spec)
  const inputType = inputTypeMap[key]
  if (!inputType) return { type: 'string' }
  if (typeof inputType === 'string') {
    if (inputType === 'select') {
      const options = (spec.enum ?? []).map((v) => ({ value: v, label: v }))
      return { type: inputType, options }
    }
    return { type: inputType }
  }
  const stringContentType = spec.stringContentType ?? 'string'
  const stringType: FormInputType =
    (inputType[stringContentType as keyof typeof inputType] as FormInputType) ?? 'string'
  return { type: stringType }
}

export interface BuildFieldPropsOptions {
  idPrefix: string
  requiredKeys: Set<string>
}

export function buildFieldProps(
  key: string,
  fieldSpec: SurveyFieldSpec,
  config: FieldInputConfig,
  options: BuildFieldPropsOptions,
): Record<string, unknown> {
  const { idPrefix, requiredKeys } = options
  const type = config.type
  const base: Record<string, unknown> = {
    id: idPrefix + key,
    description: fieldSpec.description,
    label: fieldSpec.title ?? key,
    maxlength: fieldSpec.maxLength,
    minlength: fieldSpec.minLength,
    required: requiredKeys.has(key),
    type,
  }
  if (type === 'number') {
    if (typeof fieldSpec.min === 'number') base.min = fieldSpec.min
    if (typeof fieldSpec.max === 'number') base.max = fieldSpec.max
  }
  if (type === 'select') base.options = config.options ?? []
  return base
}

export function getFormFieldsFromSpec(spec: { properties?: Record<string, SurveyFieldSpec> }): {
  key: string
  fieldSpec: SurveyFieldSpec
  config: FieldInputConfig
}[] {
  const properties = spec.properties ?? {}
  return Object.entries(properties)
    .sort(([, a], [, b]) => (a.propertyOrder ?? 0) - (b.propertyOrder ?? 0))
    .map(([key, fieldSpec]) => ({
      key,
      fieldSpec,
      config: getFieldInputConfig(fieldSpec),
    }))
}

export function getMappedFieldsFromSpec(
  spec: { properties?: Record<string, SurveyFieldSpec> },
  idPrefix: string,
  requiredKeys: Set<string>,
): { key: string; props: Record<string, unknown> }[] {
  const fields = getFormFieldsFromSpec(spec)
  return fields.map(({ key, fieldSpec, config }) => ({
    key,
    props: buildFieldProps(key, fieldSpec, config, { idPrefix, requiredKeys }),
  }))
}
